import { useEffect, useState, useContext, useRef, useCallback } from "react";
import { Tree } from "primereact/tree";
import { useTranslation } from "react-i18next";
import "primereact/resources/themes/bootstrap4-light-blue/theme.css";
import "primereact/resources/primereact.min.css";
import useApi from "../../../scripts/apiService";
import TreeContext from "../../../context/treeContext";
import { useToken } from "../../../scripts/tokenContext";
import { useMarquee } from "./useMarquee";

function ResourcesTree() {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [expandedKeys, setExpandedKeys] = useState({});
  const { fetchData } = useApi();
  // `treeFilter` não entra aqui: o filtro de ativo vive na Home e aponta para
  // nós da árvore de ATIVOS. As chaves desta árvore são de outro espaço
  // (`client-*`, `resource-type-*`, `${recurso}-${id}`), então o filtro nunca
  // casaria com um nó daqui — e esta árvore só é renderizada na tela de
  // Recursos, onde o filtro não atua.
  const { setTreeSelected, treeSelected, treeData, setTreeData } =
    useContext(TreeContext);
  const initialFetchDone = useRef(false);
  const { permissions } = useToken();

  const hasResourceCreationPermission = useCallback(
    (requestedPermissions) => {
      if (!permissions) return false;
      const requested = Array.isArray(requestedPermissions)
        ? requestedPermissions
        : [requestedPermissions];
      return requested.some((slug) => permissions.some((p) => p.slug === slug));
    },
    [permissions],
  );

  const getResourceCreationPermissions = useCallback((resource) => {
    switch (resource) {
      case "pumps":
        return ["create_pumps", "delete_pumps"];
      case "greases":
        return ["create_greases", "delete_greases"];
      case "operators":
        return ["create_lubricators", "delete_lubricators"];
      /* istanbul ignore next -- defensive: resource is always pumps/greases/operators (resource-type nodes are only built with those), so the default is unreachable */
      default:
        return [];
    }
  }, []);

  const canCreateResource = useCallback(
    (resource) =>
      hasResourceCreationPermission(getResourceCreationPermissions(resource)),
    [getResourceCreationPermissions, hasResourceCreationPermission],
  );

  const getResourceReadPermission = useCallback((resource) => {
    switch (resource) {
      case "operators":
        return "read_lubricators";
      default:
        return null;
    }
  }, []);

  const canReadResource = useCallback(
    (resource) => {
      const permission = getResourceReadPermission(resource);
      return permission
        ? (permissions?.some((item) => item.slug === permission) ?? false)
        : true;
    },
    [getResourceReadPermission, permissions],
  );

  const getResourceLabel = useCallback(
    (resource) => {
      switch (resource) {
        case "pumps":
          return t("Bombas");
        case "greases":
          return t("Graxas");
        case "operators":
          return t("Lubrificadores");
        /* istanbul ignore next -- defensive: getResourceLabel only relabels resource-type nodes, whose resource is always pumps/greases/operators, so the default is unreachable */
        default:
          return resource;
      }
    },
    [t],
  );

  const getCreateResourceLabel = useCallback(
    (resource) => {
      const resourceLabel =
        resource === "pumps"
          ? t("Bomba")
          : resource === "greases"
            ? t("Graxa")
            : t("Lubrificador");

      return `+ ${t("Criar")} ${resourceLabel}`;
    },
    [t],
  );

  const relabelResourceNodes = useCallback(
    (nodes = []) =>
      nodes.map((node) => {
        const nextNode = { ...node };

        if (node.data?.type === "resource-type" && node.data?.resource) {
          nextNode.label = getResourceLabel(node.data.resource);
        }

        if (
          typeof node.data?.type === "string" &&
          node.data.type.startsWith("create-")
        ) {
          const resource = node.data.type.replace("create-", "");
          nextNode.label = getCreateResourceLabel(resource);
        }

        if (node.children) {
          nextNode.children = relabelResourceNodes(node.children);
        }

        return nextNode;
      }),
    [getCreateResourceLabel, getResourceLabel],
  );

  useEffect(() => {
    if (initialFetchDone.current) return;
    initialFetchDone.current = true;
    setTreeSelected(null);

    const fetchRootResources = async () => {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("user"));
      const clientId = user?.user?.client?.id;

      try {
        if (!clientId) {
          const clientsResp = await fetchData("/clients");
          const clients = clientsResp.data || [];

          clients.sort((a, b) => {
            const nameA = (a.name || a.code || "").toLowerCase();
            const nameB = (b.name || b.code || "").toLowerCase();
            return nameA.localeCompare(nameB);
          });

          const rootNodes = clients.map((client) => {
            const children = [
              {
                key: `resource-type-pumps-${client.id}`,
                label: t("Bombas"),
                leaf: false,
                data: {
                  type: "resource-type",
                  resource: "pumps",
                  clientId: client.id,
                  client,
                },
              },
              {
                key: `resource-type-greases-${client.id}`,
                label: t("Graxas"),
                leaf: false,
                data: {
                  type: "resource-type",
                  resource: "greases",
                  clientId: client.id,
                  client,
                },
              },
              {
                key: `resource-type-operators-${client.id}`,
                label: t("Lubrificadores"),
                leaf: false,
                data: {
                  type: "resource-type",
                  resource: "operators",
                  clientId: client.id,
                  client,
                },
              },
            ].filter((node) => canReadResource(node.data.resource));

            return {
              key: `client-${client.id}`,
              label: client?.name
                ? `${client?.name} (${client?.code})`
                : client?.code,
              leaf: false,
              data: { type: "client", clientId: client.id, client },
              children,
            };
          });

          setTreeData(rootNodes);
        } else {
          const rootNodes = [
            {
              key: "resource-type-pumps",
              label: t("Bombas"),
              leaf: false,
              data: { type: "resource-type", resource: "pumps", clientId },
            },
            {
              key: "resource-type-greases",
              label: t("Graxas"),
              leaf: false,
              data: { type: "resource-type", resource: "greases", clientId },
            },
            {
              key: "resource-type-operators",
              label: t("Lubrificadores"),
              leaf: false,
              data: { type: "resource-type", resource: "operators", clientId },
            },
          ].filter((node) => canReadResource(node.data.resource));
          setTreeData(rootNodes);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchRootResources();
  }, [canReadResource, fetchData, setTreeData, setTreeSelected, t]);

  useEffect(() => {
    setTreeData((prevTreeData) => relabelResourceNodes(prevTreeData));
  }, [i18n.language, relabelResourceNodes, setTreeData]);

  const loadOnExpand = async (event) => {
    const nodeData = event.node.data;

    if (!nodeData || nodeData.type !== "resource-type") return;

    const alreadyLoaded = event.node.children?.some(
      (child) => !child.data?.type?.startsWith("create-"),
    );
    if (alreadyLoaded) return;

    setLoading(true);

    try {
      const clientId = nodeData.clientId;
      const resource = nodeData.resource;

      let endpoint = "";
      if (resource === "pumps") endpoint = "/pumps";
      else if (resource === "greases") endpoint = "/greases";
      else if (resource === "operators") endpoint = "/lubricators";

      const response = await fetchData(endpoint, { client_id: clientId });
      const items = response.data || [];

      const children = items
        .map((item) => ({
          key: `${resource}-${item.id}`,
          label: item.name || item.code || `(${item.id})`,
          leaf: true,
          data: { ...item, type: "resource-item", resource },
        }))
        .sort((a, b) => a.label.localeCompare(b.label));

      const canCreate = canCreateResource(resource);

      const finalChildren = canCreate
        ? [
            {
              key: `create-${resource}-${event.node.key}`,
              label: getCreateResourceLabel(resource),
              leaf: true,
              data: { type: `create-${resource}`, parent: event.node.data },
            },
            ...children,
          ]
        : children;

      if (finalChildren.length === 0 && !canCreate) {
        delete event.node.children;
        event.node.leaf = true;
        setExpandedKeys((prev) => {
          const next = { ...prev };
          delete next[event.node.key];
          return next;
        });
      } else {
        event.node.children = finalChildren;
        event.node.leaf = false;
      }

      setTreeData([...treeData]);
    } catch (err) {
      console.error("Erro ao carregar recursos:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleNodeSelect = (event) => {
    const selectedNode = event.node;
    if (
      typeof treeSelected === "object" &&
      treeSelected?.key === selectedNode.key
    ) {
      setTreeSelected(null);
      return;
    }

    setTreeSelected(selectedNode);
  };

  const useNodeTemplate = (node) => {
    const { outerRef, innerRef, marquee, marqueeVars } = useMarquee(node.label);

    const isSelected =
      typeof treeSelected === "string"
        ? treeSelected === node.key
        : treeSelected?.key === node.key;

    return (
      <span
        ref={outerRef}
        className={`tree-node-label${isSelected ? " selected-node" : ""}${
          marquee.overflow ? " is-overflow" : ""
        }`}
        style={{
          display: "inline-block",
          whiteSpace: "nowrap",
          maxWidth: "var(--tree-node-label-max-width)",
          overflow: "hidden",
          verticalAlign: "middle",
          position: "relative",
        }}
        title={node.label}
      >
        <span
          ref={innerRef}
          className="tree-node-label-inner"
          style={{
            display: "inline-block",
            whiteSpace: "nowrap",
            ...marqueeVars,
          }}
        >
          {node.label}
        </span>
      </span>
    );
  };

  return (
    <Tree
      value={treeData}
      expandedKeys={expandedKeys}
      onToggle={(e) => setExpandedKeys(e.value)}
      selectionMode="single"
      onExpand={loadOnExpand}
      loading={loading}
      selectionKeys={treeSelected ? { [treeSelected.key]: true } : {}}
      onSelectionChange={(e) => {
        const selectedKey =
          typeof e.value === "string" ? e.value : Object.keys(e.value || {})[0];
        const selectedNode = findNodeByKey(treeData, selectedKey);

        if (selectedNode?.data?.type === "client") {
          return;
        }

        if (selectedNode) {
          handleNodeSelect({ node: selectedNode });
        } else {
          /* istanbul ignore next -- defensive: with selectionMode="single" the emitted key always belongs to a rendered node (found in treeData); real deselection goes through handleNodeSelect */
          setTreeSelected(null);
        }
      }}
      nodeTemplate={useNodeTemplate}
      emptyMessage={t("Nenhum recurso disponível.")}
    />
  );
}

export default ResourcesTree;

function findNodeByKey(nodes, key) {
  /* istanbul ignore next -- defensive: callers always pass a truthy key from a rendered node, and the recursive call is guarded by `if (node.children)`, so key/nodes are never falsy here */
  if (!key || !nodes) return null;
  for (const node of nodes) {
    if (node.key === key) return node;
    if (node.children) {
      const found = findNodeByKey(node.children, key);
      if (found) return found;
    }
  }
  return null;
}
